import { useState, useEffect, useRef } from 'react';
import { Image, Upload, ToggleLeft, ToggleRight, Save, X, AlertCircle } from 'lucide-react';
import { fetchPromotionalBanner, updatePromotionalBanner, toggleBannerStatus } from '../api/placeholder';

export default function PromotionalBanner() {
  // Current banner state (from backend)
  const [currentBanner, setCurrentBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [caption, setCaption] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const fileInputRef = useRef(null);

  // Fetch current banner on mount
  useEffect(() => {
    loadCurrentBanner();
  }, []);

  const loadCurrentBanner = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPromotionalBanner();
      setCurrentBanner(data);
      setCaption(data.caption || '');
      setIsActive(data.active || false);
    } catch (err) {
      setError('Failed to load current banner');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setMessage({ type: 'error', text: 'Please select a PNG or JPG image' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMessage(null);
  };

  // Clear selected file
  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle save/update
  const handleSave = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select an image to upload' });
      return;
    }

    if (!caption.trim()) {
      setMessage({ type: 'error', text: 'Please enter a caption for the banner' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await updatePromotionalBanner(selectedFile, caption, isActive);
      setMessage({ type: 'success', text: 'Banner updated successfully' });
      clearSelectedFile();
      // Reload current banner
      await loadCurrentBanner();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update banner. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Handle status toggle (for existing banner)
  const handleToggleStatus = async () => {
    const newStatus = !isActive;
    setIsActive(newStatus);

    try {
      await toggleBannerStatus(newStatus);
      setMessage({ type: 'success', text: `Banner ${newStatus ? 'activated' : 'deactivated'} successfully` });
    } catch (err) {
      setIsActive(!newStatus); // Revert on error
      setMessage({ type: 'error', text: 'Failed to update banner status' });
    }
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-neon-cyan border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Promotional Banner</h1>
        <p className="text-sm text-slate-500">
          Manage the banner sent to users after completing their 7th milestone assessment
        </p>
      </div>

      {message && (
        <div
          className={`mb-6 rounded-xl px-4 py-3 text-sm border flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-neon-green/10 text-neon-green border-neon-green/30'
              : 'bg-neon-pink/10 text-neon-pink border-neon-pink/30'
          }`}
        >
          <AlertCircle size={16} />
          {message.text}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload / Edit Section */}
        <div className="space-y-6">
          {/* Banner Upload */}
          <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6">
            <div className="mb-4 flex items-center gap-2 text-neon-cyan">
              <Upload size={18} />
              <h2 className="text-sm font-semibold">Upload Banner</h2>
            </div>

            <div className="space-y-4">
              {/* File Input */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="banner-upload"
                />
                <label
                  htmlFor="banner-upload"
                  className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-surface-500 bg-surface-700/50 p-8 transition-all hover:border-neon-cyan/50 hover:bg-surface-700"
                >
                  <Image size={32} className="mb-2 text-slate-500" />
                  <p className="text-sm font-medium text-slate-300">
                    Click to select an image
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    PNG or JPG, max 5MB
                  </p>
                </label>
              </div>

              {/* Selected File Preview */}
              {selectedFile && previewUrl && (
                <div className="relative">
                  <div className="rounded-xl border border-surface-600 bg-surface-700 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-400 truncate max-w-[200px]">
                        {selectedFile.name}
                      </p>
                      <button
                        onClick={clearSelectedFile}
                        className="text-slate-500 hover:text-neon-pink transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full rounded-lg object-contain max-h-48"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Uploading will replace the existing banner
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Caption Input */}
          <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6">
            <div className="mb-4 flex items-center gap-2 text-neon-cyan">
              <span className="text-lg">💬</span>
              <h2 className="text-sm font-semibold">Banner Caption</h2>
            </div>

            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Enter the caption that will be sent with the banner..."
              rows={4}
              className="w-full rounded-xl border border-surface-600 bg-surface-700 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-500 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all resize-none"
            />
            <p className="mt-2 text-xs text-slate-500">
              This caption will be sent together with the banner image on WhatsApp
            </p>
          </div>

          {/* Status Toggle */}
          <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6">
            <div className="mb-4 flex items-center gap-2 text-neon-cyan">
              {isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
              <h2 className="text-sm font-semibold">Banner Status</h2>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-200">
                  {isActive ? 'Active' : 'Inactive'}
                </p>
                <p className="text-xs text-slate-500">
                  {isActive
                    ? 'Banner will be sent after assessments'
                    : 'Banner will not be sent'}
                </p>
              </div>
              <button
                onClick={handleToggleStatus}
                className={`relative h-7 w-12 rounded-full transition-colors ${
                  isActive ? 'bg-neon-green' : 'bg-surface-600'
                }`}
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${
                    isActive ? 'left-6' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !selectedFile}
            className="w-full rounded-xl bg-neon-cyan/20 border border-neon-cyan/40 py-3 text-sm font-semibold text-neon-cyan hover:bg-neon-cyan/30 hover:shadow-neon-cyan transition-all focus:outline-none focus:ring-2 focus:ring-neon-cyan/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent" />
                Uploading...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Banner
              </>
            )}
          </button>
        </div>

        {/* Current Banner Preview */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6">
          <div className="mb-4 flex items-center gap-2 text-neon-cyan">
            <Image size={18} />
            <h2 className="text-sm font-semibold">Current Banner Preview</h2>
          </div>

          {currentBanner?.imageUrl ? (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                    isActive
                      ? 'bg-neon-green/15 text-neon-green border border-neon-green/30'
                      : 'bg-slate-500/15 text-slate-400 border border-slate-500/30'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isActive ? 'bg-neon-green' : 'bg-slate-500'
                    }`}
                  />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Banner Image */}
              <div className="rounded-xl border border-surface-600 bg-surface-700 p-3">
                <img
                  src={currentBanner.imageUrl}
                  alt="Current Banner"
                  className="h-64 w-auto rounded-lg object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              {/* Caption Preview */}
              {caption && (
                <div className="rounded-xl border border-surface-600 bg-surface-700 p-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Caption:</p>
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{caption}</p>
                </div>
              )}

              {/* WhatsApp Preview */}
              <div className="rounded-xl border border-neon-green/30 bg-neon-green/5 p-4">
                <p className="text-xs font-medium text-neon-green mb-3">
                  WhatsApp Preview
                </p>
                <div className="bg-[#075E54] rounded-lg p-3 max-w-[280px]">
                  <img
                    src={currentBanner.imageUrl}
                    alt="WhatsApp Preview"
                    className="w-full rounded-md mb-2"
                  />
                  {caption && (
                    <p className="text-xs text-white whitespace-pre-wrap">{caption}</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Image size={48} className="mb-3 text-slate-600" />
              <p className="text-sm text-slate-400">No banner uploaded yet</p>
              <p className="mt-1 text-xs text-slate-500">
                Upload a banner to see the preview
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
